let connectionPromise: Promise<IDBDatabase> | undefined;

const BASE_STORE_CONFIG: [any, Array<[string, any]>] = [
  { keyPath: 'keyPath' },
  [
    ['args', {unique: false}],
    ['accountId', {unique: false}],
  ]
];

const stores: Array<[string, any, Array<[string, any]>]> = [
  ['timesec/access-tokens', {keyPath: 'id'}, []],
  ['store/accounts/institutions/settings', ...BASE_STORE_CONFIG],
  ['store/accounts/institutions/settings-historical', ...BASE_STORE_CONFIG],
  ['store/employees/employees/roles', ...BASE_STORE_CONFIG],
  ['store/employees/employees/settings', ...BASE_STORE_CONFIG],
  ['store/employees/employees/settings-historical', ...BASE_STORE_CONFIG],
  ['store/timestamps/workmonths', ...BASE_STORE_CONFIG],
  ['store/reports/employees', ...BASE_STORE_CONFIG],
  ['store/daily-reports/entries', ...BASE_STORE_CONFIG],
  ['store/daily-reports/entries-history', ...BASE_STORE_CONFIG],
//  ['store/institutions', {keyPath: 'id'}, []],
];

function createMissingStores(db: IDBDatabase) {
  for (const [name, storeConfig, indexes] of stores) {
    if (db.objectStoreNames.contains(name)) continue;
    const objectStore = db.createObjectStore(name, storeConfig);
    for (const [index, indexConfig] of indexes) objectStore.createIndex(index, index, indexConfig);
  }
}

function openDb(targetVersion?: number): Promise<IDBDatabase> {
  return new Promise<IDBDatabase>((resolve, reject) => {
    const request = targetVersion === undefined
      ? window.indexedDB.open('zeit-db')
      : window.indexedDB.open('zeit-db', targetVersion);

    request.onupgradeneeded = () => createMissingStores(request.result);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('Cannot open indexedDB connection'));
    request.onblocked = () => reject(request.error ?? new Error('indexedDB open blocked'));
  });
}

async function connect(): Promise<IDBDatabase> {
  // Open at whatever version the DB currently is (creating it, and all stores,
  // if it doesn't exist yet). Opening without a fixed version means we never
  // throw VersionError when the on-disk DB is ahead of the code.
  let db = await openDb();

  // A hard-coded version number is fragile: if an upgrade is interrupted — e.g.
  // an HMR/page reload lands between adding a store to `stores` and that upgrade
  // committing — the DB can sit at a version that already "used up" its number
  // while still missing a store, and reopening at the same version never
  // upgrades again. So don't trust the version: check the stores directly and
  // force one more upgrade (at version + 1) whenever any are missing. This
  // self-heals, and removes the need to manually bump a version when adding a
  // store — just add it to `stores`.
  if (stores.some(([name]) => !db.objectStoreNames.contains(name))) {
    const nextVersion = db.version + 1;
    db.close();
    db = await openDb(nextVersion);
  }

  db.onclose = () => { connectionPromise = undefined; };
  db.onversionchange = () => { db.close(); connectionPromise = undefined; };
  return db;
}

function getConnection(): Promise<IDBDatabase> {
  if (connectionPromise) return connectionPromise;

  connectionPromise = connect().catch((error) => {
    connectionPromise = undefined;
    throw error;
  });

  return connectionPromise;
}

async function getEntries<T>(objectStore): Promise<Array<T>> {
  const connection = await getConnection();
  const store = connection.transaction(objectStore).objectStore(objectStore);

  return new Promise(resolve => {
    const entries: Array<T> = [];
    store.openCursor().onsuccess = (event) => {
      const cursor = (event.target as IDBRequest<T>).result;
      if (!cursor) return;
      entries.push(cursor.value);
      cursor.continue;
    };
    resolve(entries);
  });
}

async function getEntry<T>(objectStore, id): Promise<T> {
  const connection = await getConnection();
  const store = connection.transaction(objectStore).objectStore(objectStore);
  const request = store.get(id);

  return new Promise(resolve => {
    request.onsuccess = () => resolve(request.result);
  });
}

function deleteEntry(objectStore, id): Promise<undefined> {
  return new Promise(resolve => {
    getConnection().then(connection => {
      const t = connection.transaction(objectStore, 'readwrite');
      t.objectStore(objectStore).delete(id);
      t.oncomplete = () => resolve(undefined);
    });
  });
}

function putEntry<T>(objectStore, entry: T): Promise<undefined> {
  return new Promise(resolve => {
    getConnection().then(connection => {
      const t = connection.transaction(objectStore, 'readwrite');
      t.objectStore(objectStore).put(entry);
      t.oncomplete = () => resolve(undefined);
    });
  });
}

export { getEntries, getEntry, deleteEntry, putEntry };
